
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { useBaseMfaState } from './useBaseMfaState';
import { handleMfaError } from '../types';
import { MfaEnrollmentData } from '../../types';

/**
 * Hook for MFA enrollment start operation
 * 
 * Provides functionality to start the MFA enrollment process
 * 
 * @returns MFA enrollment start function
 */
export function useMfaEnrollmentOperations() {
  const { setIsLoading } = useBaseMfaState();

  /**
   * Start the MFA enrollment process
   * 
   * This function:
   * 1. Initiates enrollment with the auth provider
   * 2. Returns enrollment data including QR code for setup
   * 3. Stores the factor in our database with unverified status
   * 4. Handles errors with user feedback
   * 
   * @returns Promise resolving to enrollment data or null on failure
   */
  const enrollMfa = async (): Promise<MfaEnrollmentData | null> => {
    try {
      setIsLoading(true);
      
      // Get current user first
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      
      // Start the enrollment process with Supabase
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
      });
      
      if (error) throw error;
      
      // Store the factor in our database with encrypted secret
      const { data: encryptedSecret, error: encryptError } = await supabase.rpc(
        'encrypt_mfa_secret',
        { secret: data.totp.secret }
      );
      
      if (encryptError) {
        console.warn('Failed to encrypt MFA secret:', encryptError);
        // Continue with fallback encryption
      }
      
      const { error: dbError } = await supabase
        .from('mfa_factors')
        .insert({
          id: data.id,
          user_id: user.id,
          factor_type: 'totp',
          secret: encryptedSecret || `enc_fallback_${Date.now()}`,
          status: 'unverified'
        });
      
      if (dbError) {
        console.warn('Failed to store MFA factor in database:', dbError);
        // Continue anyway as Supabase has the factor
      }
      
      // Transform the Supabase response to our MfaEnrollmentData type
      const enrollmentData: MfaEnrollmentData = {
        id: data.id,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
        totp: data.totp
      };
      
      return enrollmentData;
    } catch (error) {
      const mfaError = handleMfaError(error);
      console.error('MFA enrollment failed:', mfaError);
      toast({
        title: "MFA Enrollment Failed",
        description: mfaError.message || "An unknown error occurred",
        variant: "destructive"
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    enrollMfa
  };
}
